import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { InsightSeverity, OwnerInsight } from '@/infrastructure/http/metricsApi';
import {
  buildAttentionHref,
  clinicHubHref,
  preferredAttentionCta,
  resolveAccessibleAttentionCta,
  type AttentionCtaTarget,
} from '@/application/metrics/attentionCta';
import { canAccessClinicHub } from '@/application/navigation/menuHouses';
import { formatPercent } from '@/presentation/components/metrics/metricFormatters';
import { formatMoneyCompact, formatSigned } from '@/presentation/utils/metricDeltas';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';

interface AttentionInsightListProps {
  insights: OwnerInsight[];
  period: string;
  clinicNames: Record<string, string>;
  /** Hub de una clínica: no agrupar por nombre de otra clínica. */
  hideClinicName?: boolean;
  emptyMessage?: string;
  /** @deprecated la lista de red ya es compacta; se ignora para estilos. */
  compact?: boolean;
  /** Máximo de clínicas visibles en panel (default 3 en red; sin límite en hub). */
  maxVisible?: number;
  /** Título de sección + CTA “ver todas” en la misma fila. */
  showSectionTitle?: boolean;
}

type ClinicAttentionGroup = {
  tenantId: string;
  severity: InsightSeverity;
  insights: OwnerInsight[];
  summaryParts: string[];
  cta: AttentionCtaTarget | null;
  href: string | null;
  ctaLabel: string | null;
  secondaryHref: string | null;
};

function severityRank(severity: InsightSeverity): number {
  if (severity === 'critical') return 0;
  if (severity === 'warning') return 1;
  return 2;
}

function worstSeverity(a: InsightSeverity, b: InsightSeverity): InsightSeverity {
  return severityRank(a) <= severityRank(b) ? a : b;
}

/**
 * Atención requerida: filas compactas por clínica con ≥1 CTA (tenant + periodo).
 */
export function AttentionInsightList({
  insights,
  period,
  clinicNames,
  hideClinicName = false,
  emptyMessage,
  maxVisible,
  showSectionTitle = false,
}: AttentionInsightListProps) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const locale = i18n.language;
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!showAll) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowAll(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAll]);

  const limit = maxVisible ?? (hideClinicName ? 50 : 3);

  const groups = useMemo(() => {
    const byTenant = new Map<string, OwnerInsight[]>();

    for (const insight of insights) {
      const list = byTenant.get(insight.tenantId) ?? [];
      list.push(insight);
      byTenant.set(insight.tenantId, list);
    }

    const result: ClinicAttentionGroup[] = [];

    for (const [tenantId, tenantInsights] of byTenant) {
      const severity = tenantInsights.reduce(
        (acc, item) => worstSeverity(acc, item.severity),
        'info' as InsightSeverity
      );

      const summaryParts: string[] = [];
      for (const insight of tenantInsights) {
        if (insight.type === 'pvp_drop') {
          const pvp =
            typeof insight.metrics.pvp === 'number'
              ? insight.metrics.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })
              : null;
          const deltaAbs =
            insight.metrics.deltaAbs === null || insight.metrics.deltaAbs === undefined
              ? null
              : Number(insight.metrics.deltaAbs);
          const prosRaw = insight.metrics.professionalsInAttention;
          const prosCount =
            typeof prosRaw === 'number'
              ? prosRaw
              : insight.subjects.length > 0
                ? insight.subjects.length
                : 0;

          if (deltaAbs !== null && deltaAbs <= -0.3 && pvp !== null) {
            summaryParts.push(
              t('dashboard.command.attention.compact.pvpDrop', {
                pvp,
                delta: formatSigned(locale, deltaAbs),
              })
            );
          } else if (pvp !== null && deltaAbs !== null) {
            summaryParts.push(
              t('dashboard.command.attention.compact.pvpLevel', {
                pvp,
                delta: formatSigned(locale, deltaAbs),
              })
            );
          }

          if (prosCount > 0) {
            summaryParts.push(
              t('dashboard.command.attention.compact.pros', { count: prosCount })
            );
          }
        } else if (insight.type === 'revenue_drop') {
          const delta =
            insight.metrics.deltaPct === null || insight.metrics.deltaPct === undefined
              ? '—'
              : `${formatSigned(locale, Number(insight.metrics.deltaPct))}%`;
          const current = formatMoneyCompact(locale, Number(insight.metrics.revenue ?? 0));
          const previous = formatMoneyCompact(locale, Number(insight.metrics.revenuePrevious ?? 0));
          summaryParts.push(
            t('dashboard.command.attention.compact.revenue', { delta, current, previous })
          );
        } else {
          const current = formatPercent(Number(insight.metrics.absenteeism ?? 0), locale);
          const delta = formatSigned(locale, Number(insight.metrics.deltaPp ?? 0));
          summaryParts.push(
            t('dashboard.command.attention.compact.absenteeism', { current, delta })
          );
        }
      }

      const preferred = preferredAttentionCta(tenantInsights);
      const cta = resolveAccessibleAttentionCta(preferred, hasPermission);
      const href = cta
        ? buildAttentionHref({ cta, tenantId, period, hideClinicName })
        : null;

      const ctaLabel =
        cta === 'finances'
          ? t('dashboard.command.attention.openFinances')
          : cta === 'professionals'
            ? t('dashboard.command.attention.openProfessionals')
            : cta === 'patients'
              ? t('dashboard.command.attention.openPatients')
              : cta === 'clinic'
                ? hideClinicName
                  ? t('dashboard.command.attention.openLocal')
                  : t('dashboard.command.attention.openClinic')
                : null;

      const showSecondaryClinic =
        cta !== null &&
        cta !== 'clinic' &&
        !hideClinicName &&
        canAccessClinicHub(hasPermission);

      result.push({
        tenantId,
        severity,
        insights: tenantInsights,
        summaryParts: summaryParts.filter(Boolean),
        cta,
        href,
        ctaLabel,
        secondaryHref: showSecondaryClinic ? clinicHubHref(tenantId, period) : null,
      });
    }

    return result.sort(
      (a, b) =>
        severityRank(a.severity) - severityRank(b.severity) ||
        b.insights.length - a.insights.length
    );
  }, [insights, period, hideClinicName, locale, t, hasPermission]);

  if (groups.length === 0) {
    return (
      <div className="space-y-1.5">
        {showSectionTitle ? (
          <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
            {t('dashboard.executive.attention.title')}
          </h2>
        ) : null}
        <p className="rounded-md border border-dashed border-[var(--bi-line)] px-2.5 py-2 text-xs text-gray-500 dark:text-gray-400">
          {emptyMessage ?? t('dashboard.executive.attention.empty')}
        </p>
      </div>
    );
  }

  const visible = groups.slice(0, limit);
  const hiddenCount = Math.max(0, groups.length - visible.length);

  const renderRow = (group: ClinicAttentionGroup) => {
    const clinicLabel = clinicNames[group.tenantId] ?? group.tenantId;
    const alertCount = group.insights.length;
    const isCritical = group.severity === 'critical';

    return (
      <li key={group.tenantId}>
        <div className="flex w-full items-center gap-2.5 rounded-md border border-[var(--bi-line)] bg-white/70 px-2.5 py-2 dark:bg-white/[0.04]">
          <span
            className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${
              isCritical ? 'bg-red-500' : 'bg-amber-500'
            }`}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
              {!hideClinicName ? (
                <p className="truncate text-sm font-semibold text-gray-950 dark:text-white">
                  {clinicLabel}
                  {alertCount > 1 ? (
                    <span className="ml-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                      · {t('dashboard.command.attention.alertCount', { count: alertCount })}
                    </span>
                  ) : null}
                </p>
              ) : (
                <p className="text-sm font-semibold text-gray-950 dark:text-white">
                  {t('dashboard.command.attention.alertCount', { count: alertCount })}
                </p>
              )}
            </div>
            <p className="mt-0.5 truncate text-xs text-gray-600 dark:text-gray-300">
              {group.summaryParts.join(' · ') || t('dashboard.executive.attention.title')}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5 sm:flex-row sm:items-center sm:gap-2">
            {group.href && group.ctaLabel ? (
              <Link
                to={group.href}
                className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-300"
              >
                {group.ctaLabel} →
              </Link>
            ) : (
              <span className="text-[11px] text-gray-400 dark:text-gray-500">
                {t('dashboard.command.attention.noAccess')}
              </span>
            )}
            {group.secondaryHref ? (
              <Link
                to={group.secondaryHref}
                className="text-[11px] font-medium text-gray-500 hover:underline dark:text-gray-400"
              >
                {t('dashboard.command.attention.openClinic')}
              </Link>
            ) : null}
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-1.5">
      {showSectionTitle ? (
        <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
          {t('dashboard.executive.attention.title')}
        </h2>
      ) : null}

      <ul className="space-y-1">{visible.map(renderRow)}</ul>

      {hiddenCount > 0 ? (
        <div className="pt-0.5">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-300"
          >
            {t('dashboard.command.attention.viewMore', {
              remaining: hiddenCount,
              total: groups.length,
            })}{' '}
            →
          </button>
        </div>
      ) : null}

      {showAll && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label={t('dashboard.executive.attention.title')}
          onClick={() => setShowAll(false)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-[var(--bi-line)] bg-white shadow-xl dark:bg-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[var(--bi-line)] px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-950 dark:text-white">
                {t('dashboard.executive.attention.title')}
                <span className="ml-1.5 text-xs font-medium text-gray-500">
                  ({groups.length})
                </span>
              </h3>
              <button
                type="button"
                onClick={() => setShowAll(false)}
                className="rounded-md px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
              >
                {t('common.close')}
              </button>
            </div>
            <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">{groups.map(renderRow)}</ul>
          </div>
        </div>
      )}
    </div>
  );
}
